import { useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import React, { useMemo, useCallback, useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ShoppingCart, Trash2, AlertCircle, ChevronDown, Plus } from "lucide-react";
import { SelectedProductItem } from "@/components/products/SelectedProductItem";
import { ProductDetailsDialog } from "@/components/products/ProductDetailsDialog";
import { Product, ProductSelection } from "@/types/product";
import { useProductsLookup } from "@/hooks/use-products";
import { useProductSummary } from "@/hooks/use-product-summary";
import { formatCentsAsDollars } from "@/lib/price-utils";
import { useRecommendations } from "@/hooks/use-recommendations";
import { useProductEvents } from "@/hooks/use-product-events";
import {
  RECOMMENDATIONS_DISPLAY_LIMIT,
  RECOMMENDATIONS_MIN_ITEMS,
  RECOMMENDATIONS_MIN_CART_VALUE,
} from "@/constants/recommendations";
// ProductSelectionModal removed - now handled by separate ProductSelectionButton component



interface SelectedProductsListProps {
  selectedProducts?: ProductSelection[];
  onProductQuantityChange: (productId: string, quantity: number) => void;
  onProductRemove: (productId: string) => void;
  onClearAll: () => void;
  /** When provided, called when Add is clicked on a suggested product not yet in cart (so parent can add it). */
  onAddProduct?: (product: Product, quantity: number) => void;
  showPricing?: boolean;
  disabled?: boolean;
  title?: string;
  className?: string;
  // Products data needed for display only
  products?: Product[];
  productsLoading?: boolean;
}

export const SelectedProductsList: React.FC<SelectedProductsListProps> = ({
  selectedProducts,
  onProductQuantityChange,
  onProductRemove,
  onClearAll,
  onAddProduct,
  showPricing = false,
  disabled = false,
  title = i18n.t("components.products.selectedProductsList.selectedProducts"),
  className,
  products = [],
  productsLoading = false,
}) => {
  const { t } = useTranslation();
  const { capture } = useProductEvents({ component: "SelectedProductsList" });
  const [suggestedProductForDialog, setSuggestedProductForDialog] =
    useState<Product | null>(null);
  const [suggestedDialogOpen, setSuggestedDialogOpen] = useState(false);
  // Extract product IDs from selected products to fetch their details
  // This ensures that when a template is loaded, we fetch the specific products it references
  const productIds = useMemo(
    () => (selectedProducts || []).map((sp) => sp.productId),
    [selectedProducts]
  );

  // Fetch product details for selected products using useProductsLookup
  // This hook will fetch products from the API if they're not in the cache,
  // solving the issue where template products weren't in initialProducts
  const { products: fetchedProducts, isLoading: isFetchingProducts } =
    useProductsLookup(productIds);

  // Calculate summary information using the shared hook
  const summary = useProductSummary({
    selectedProducts: selectedProducts?.map((p) => ({
      quantity: p.quantity,
      unitPrice: "unitPrice" in p ? p.unitPrice : undefined,
    })),
    showPricing,
  });

  const showSuggested =
    summary.totalItems >= RECOMMENDATIONS_MIN_ITEMS ||
    (showPricing && summary.totalValue >= RECOMMENDATIONS_MIN_CART_VALUE);
  const { recommendedIds, isLoading: recommendationsLoading } =
    useRecommendations(productIds, showSuggested);
  const { products: suggestedProducts, isLoading: suggestedProductsLoading } =
    useProductsLookup(recommendedIds, showSuggested && recommendedIds.length > 0);
  const suggestedLoading = recommendationsLoading || suggestedProductsLoading;
  const displayedSuggestedProducts = useMemo(
    () => suggestedProducts.slice(0, RECOMMENDATIONS_DISPLAY_LIMIT),
    [suggestedProducts],
  );

  // Stable ID for the current recommendation set (symmetry with searchId for add_searched_product)
  const suggestionIdRef = useRef("");
  const prevRecommendedIdsRef = useRef<string[]>([]);
  useEffect(() => {
    if (recommendedIds.length > 0) {
      const prev = prevRecommendedIdsRef.current;
      const idsChanged =
        prev.length !== recommendedIds.length ||
        prev.some((id, i) => id !== recommendedIds[i]);
      if (idsChanged) {
        suggestionIdRef.current = `suggestion_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        prevRecommendedIdsRef.current = [...recommendedIds];
      }
    } else {
      suggestionIdRef.current = "";
      prevRecommendedIdsRef.current = [];
    }
  }, [recommendedIds]);

  // Merge fetched products with any products passed via props
  const allProducts = useMemo(() => {
    const productsMap = new Map<string, Product>();

    // Add products from props first (from ProductCatalog browsing)
    products.forEach((p) => productsMap.set(p.uid, p));

    // Add fetched products (from template or direct lookup)
    fetchedProducts.forEach((p) => productsMap.set(p.uid, p));

    return productsMap;
  }, [products, fetchedProducts]);

  const getProductDetails = useCallback(
    (selectedProduct: ProductSelection): Product | undefined => {
      return allProducts.get(selectedProduct.productId);
    },
    [allProducts]
  );

  // Consider products still loading if either prop loading or fetching
  const isLoadingProducts = productsLoading || isFetchingProducts;

  const handleQuantityChange = useCallback(
    (productId: string, quantity: number) => {
      onProductQuantityChange(productId, quantity);
    },
    [onProductQuantityChange]
  );

  const handleRemoveProduct = useCallback(
    (productId: string) => {
      onProductRemove(productId);
    },
    [onProductRemove]
  );

  const handleSuggestedQuantityChange = useCallback(
    (product: Product, quantity: number, index?: number) => {
      const currentQty =
        selectedProducts?.find((p) => p.productId === product.uid)?.quantity ??
        0;

      if (currentQty > 0) {
        onProductQuantityChange(product.uid, quantity);
        return;
      }

      if (quantity > 0 && index != null) {
        const suggestionId = suggestionIdRef.current;
        if (suggestionId) {
          capture({
            event: "add_suggested_product" as const,
            context: {
              productId: product.uid,
              rank: index + 1,
              suggestionId,
              price: product.currentPrice ?? undefined,
            },
          });
        }
      }

      if (onAddProduct) {
        onAddProduct(product, quantity);
      } else {
        onProductQuantityChange(product.uid, quantity);
      }
    },
    [capture, onAddProduct, onProductQuantityChange, selectedProducts],
  );

  if (!selectedProducts || selectedProducts.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base font-medium">
            <ShoppingCart className="h-4 w-4" />
            {title} (0)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>{t("components.products.selectedProductsList.noProductsSelected")}</p>
            <p className="text-sm">
              {t("components.products.selectedProductsList.useTheProductSelection")}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const suggestedDialogProductIndex = suggestedProductForDialog
    ? displayedSuggestedProducts.findIndex(
        (product) => product.uid === suggestedProductForDialog.uid,
      )
    : -1;
  const suggestedDialogQuantity = suggestedProductForDialog
    ? (selectedProducts.find(
        (product) => product.productId === suggestedProductForDialog.uid,
      )?.quantity ?? 0)
    : 0;

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base font-medium">
            <ShoppingCart className="h-4 w-4" />
            {title} ({selectedProducts?.length || 0})
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClearAll}
              disabled={disabled}
              className="text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Selected Products List */}
        <div className="relative">
          {(selectedProducts?.length || 0) > 4 && (
            <div
              className="absolute bottom-2 right-2 text-xs text-white font-semibold bg-slate-800 px-2 py-1 rounded-full shadow-lg z-10 flex items-center gap-1 transition-opacity duration-200"
              style={{ opacity: "var(--scroll-badge-opacity, 1)" }}
            >
              <ChevronDown className="h-3 w-3" />
              {t("components.products.selectedProductsList.scrollForMore")}
            </div>
          )}
          <div
            className="space-y-3 max-h-[32rem] overflow-auto"
            onScroll={(e) => {
              const target = e.target as HTMLDivElement;
              const { scrollTop, scrollHeight, clientHeight } = target;
              const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5;

              const container = target.parentElement;
              if (container) {
                container.style.setProperty(
                  "--scroll-badge-opacity",
                  isAtBottom ? "0" : "1"
                );
              }
            }}
          >
            {(selectedProducts || []).map((selectedProduct, index) => {
              const productDetails = getProductDetails(selectedProduct);
              // Use productId if available, otherwise fall back to index for missing productId edge cases
              const itemKey =
                selectedProduct.productId || `missing-id-${index}`;

              if (!productDetails) {
                // If products are still loading, show a loading placeholder instead of error
                if (isLoadingProducts) {
                  return (
                    <div
                      key={itemKey}
                      className="p-3 border rounded-lg bg-gray-50"
                    >
                      <div className="flex items-center gap-2 text-gray-500">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"></div>
                        <span className="text-sm">
                          {t("components.products.selectedProductsList.loadingProductDetails")}
                        </span>
                      </div>
                    </div>
                  );
                }

                // Only show error if products are loaded but product is still not found
                return (
                  <div
                    key={itemKey}
                    className="p-3 border rounded-lg bg-red-50"
                  >
                    <div className="flex items-center gap-2 text-red-600">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm">
                        {t("components.products.selectedProductsList.productNotFoundId")}{" "}
                        {selectedProduct.productId || "unknown"})
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          handleRemoveProduct(selectedProduct.productId)
                        }
                        className="ml-auto text-red-600 hover:text-red-800"
                        disabled={!selectedProduct.productId}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                );
              }

              return (
                <SelectedProductItem
                  key={itemKey}
                  selectedProduct={selectedProduct}
                  product={productDetails}
                  onQuantityChange={(quantity) =>
                    handleQuantityChange(selectedProduct.productId, quantity)
                  }
                  onRemove={() =>
                    handleRemoveProduct(selectedProduct.productId)
                  }
                  showPricing={showPricing}
                  disabled={disabled}
                />
              );
            })}
          </div>
        </div>

        {/* Summary Section */}
        <Separator className="my-4" />
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">{t("components.products.selectedProductsList.totalItems")}</span>
            <span className="font-medium">{summary.totalItems}</span>
          </div>

          {showPricing && summary.totalValue > 0 && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                {summary.hasMissingPrices ? t("components.products.selectedProductsList.partialTotal") : t("components.products.selectedProductsList.totalValue")}
              </span>
              <span className="font-bold text-lg text-primary">
                ${summary.totalValue.toFixed(2)}
                {summary.hasMissingPrices ? "+" : ""}
              </span>
            </div>
          )}

          {showPricing && summary.totalValue === 0 && (
            <div className="flex items-center gap-2 text-amber-600 bg-amber-50 rounded-lg p-2">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">
                {t("components.products.selectedProductsList.pricingWillBeCalculated")}
              </span>
            </div>
          )}

          {showPricing &&
            summary.hasMissingPrices &&
            summary.totalValue > 0 && (
              <div className="flex items-center gap-2 text-amber-600 text-xs">
                <AlertCircle className="h-3 w-3" />
                <span>{t("components.products.selectedProductsList.somePricesPendingTotal")}</span>
              </div>
            )}

          {/* Suggested items: only show when loading is done and we have products */}
          {showSuggested && !suggestedLoading && suggestedProducts.length > 0 && (
            <div className="pt-3 mt-3 border-t border-border">
              <p className="text-sm font-medium text-muted-foreground mb-2">
                {t("components.products.selectedProductsList.frequentlyBoughtTogether")}
              </p>
              <ul className="space-y-2">
                  {/* suggestedProducts is ordered by score (best first) from API + useProductsLookup; show top N */}
                  {displayedSuggestedProducts.map((product, index) => {
                    const currentQty =
                      selectedProducts?.find(
                        (p) => p.productId === product.uid,
                      )?.quantity ?? 0;
                    const handleAddClick = () => {
                      handleSuggestedQuantityChange(
                        product,
                        currentQty + 1,
                        index,
                      );
                    };
                    return (
                      <li
                        key={product.uid}
                        className="flex items-center gap-3 p-2 rounded-lg border border-border bg-muted/30"
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setSuggestedProductForDialog(product);
                            setSuggestedDialogOpen(true);
                          }}
                          className="w-12 h-12 shrink-0 rounded-md overflow-hidden bg-muted flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                          aria-label={`View details for ${product.description}`}
                        >
                          {product.imageUrl ? (
                            <img
                              src={product.imageUrl}
                              alt=""
                              className="w-full h-full object-contain"
                            />
                          ) : (
                            <ShoppingCart className="h-5 w-5 text-muted-foreground" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSuggestedProductForDialog(product);
                            setSuggestedDialogOpen(true);
                          }}
                          className="min-w-0 flex-1 text-left focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded p-1 -m-1"
                          aria-label={`View details for ${product.description}`}
                        >
                          <p className="text-xs font-medium line-clamp-2">
                            {product.description}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t("common.id")} {product.hajocaProductId}
                            {showPricing &&
                              product.currentPrice != null &&
                              ` · $${formatCentsAsDollars(product.currentPrice)}`}
                          </p>
                        </button>
                        <div className="shrink-0">
                          <Button
                            type="button"
                            size="sm"
                            disabled={disabled}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddClick();
                            }}
                            className="bg-black text-white hover:bg-black/90"
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            {t("components.products.selectedProductsList.add")}
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
            {suggestedProductForDialog && (
              <ProductDetailsDialog
                product={suggestedProductForDialog}
                open={suggestedDialogOpen}
                onOpenChange={(open) => {
                  setSuggestedDialogOpen(open);
                  if (!open) setSuggestedProductForDialog(null);
                }}
                quantity={suggestedDialogQuantity}
                onQuantityChange={(quantity) =>
                  handleSuggestedQuantityChange(
                    suggestedProductForDialog,
                    quantity,
                    suggestedDialogProductIndex >= 0
                      ? suggestedDialogProductIndex
                      : undefined,
                  )
                }
                showCachedPricing={showPricing}
                disabled={disabled}
              />
            )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
